import React, { useRef, useCallback, useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCanvasStore, Tool } from '@/stores/canvasStore';
import { usePartnrStore } from '@/stores/partnrStore';
import { useDragToBoardStore } from '@/stores/dragToBoardStore';
import { useArrowStore, ConnectorPosition } from '@/stores/arrowStore';
import { useUndoRedo } from '@/hooks/useUndoRedo';
import { useHistoryStore } from '@/stores/historyStore';
import { useSharedBoards, SharedBoard } from '@/hooks/useSharedBoards';
import { supabase } from '@/integrations/supabase/client';
import { CanvasItem } from './CanvasItem';
import { CanvasGrid } from './CanvasGrid';
import { ClusterOverlay } from './ClusterOverlay';
import { Minimap } from './Minimap';
import { ArrowLayer, getConnectorPoint, ArrowPropertiesPanel } from './arrows';
import { DropZone } from '@/components/upload/DropZone';
import { PartnrChatWindow, PartnrSelectionPill, PartnrConnectionLines } from '@/components/partnr';
import { PartnrSelectionRect } from './PartnrSelectionRect';
import { CanvasContextMenu } from './CanvasContextMenu';
import { DragGhostPreview } from './DragGhostPreview';
import { MindBubble } from './MindBubble';
import { SyncChoiceModal } from './SyncChoiceModal';
import { DragOverlay } from './DragOverlay';
import { DragToBoardDebugButton } from '@/components/debug/DragToBoardDebugButton';
import { SharedBoardsButton, SharedBoardsModal } from '@/components/shared';
import { ClusterToggleButton } from './ClusterToggleButton';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Focus } from 'lucide-react';
import { toast } from 'sonner';
import { KeyboardShortcutsHelp } from './KeyboardShortcutsHelp';
import { markdownToHtml, isMarkdownContent } from '@/lib/markdownUtils';
import { 
  MIN_ITEM_Y, 
  FIT_PADDING_TOP, 
  FIT_PADDING_LEFT, 
  FIT_PADDING_RIGHT, 
  FIT_PADDING_BOTTOM 
} from '@/lib/canvasConstants';

interface SelectionRect {
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
}

// Inertia physics constants - tuned for weighted, controlled feel
const FRICTION = 0.88; // Lower = heavier, shorter coast (was 0.95)
const MIN_VELOCITY = 1.0; // Stop coasting sooner (was 0.5)
const VELOCITY_SCALE = 0.15; // Less responsive initial velocity (was 0.25)

// Zoom animation constants - tuned for weighted, premium feel
const ZOOM_ANIMATION_DURATION = 240; // ms — slightly longer for heavier, smoother transitions
const ZOOM_MIN = 0.1;
const ZOOM_MAX = 4;
const ZOOM_BOUNCE_OVERSHOOT = 0.02; // How far past limits before bounce-back
const ZOOM_STEP_SCALE = 0.006; // Zoom sensitivity per wheel delta unit
const ZOOM_MAX_STEP = 0.08; // Cap per-event zoom change at 8%

// Ease-out cubic for natural deceleration
const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);

// Fit-to-view animation duration - longer for buttery smooth transitions
const FIT_ANIMATION_DURATION = 500; // ms

// Viewport culling buffer - extra pixels beyond viewport to pre-render for smooth scrolling
const VIEWPORT_BUFFER = 200;

// Arrow key pan speed (when no items selected)
const ARROW_PAN_SPEED = 50;
const ARROW_PAN_SPEED_FAST = 150;

// Arrow key item move speed (when items selected)
const ARROW_MOVE_SPEED = 1;
const ARROW_MOVE_SPEED_FAST = 10;

export function InfiniteCanvas() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPanning, setIsPanning] = useState(false);
  const [isSpacePressed, setIsSpacePressed] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [isToolDragOver, setIsToolDragOver] = useState(false);
  
  // Viewport size for culling calculations
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });
  
  // Inertia/momentum state
  const velocityRef = useRef({ x: 0, y: 0 });
  const lastPanTimeRef = useRef(0);
  const lastPanPosRef = useRef({ x: 0, y: 0 });
  const inertiaAnimationRef = useRef<number | null>(null);
  
  // Batched mouse update state (rAF optimization)
  const pendingMouseRef = useRef<{ x: number; y: number } | null>(null);
  const mouseMoveRafRef = useRef<number | null>(null);
  
  // Zoom animation state
  const zoomAnimationRef = useRef<number | null>(null);
  const zoomStartRef = useRef({ zoom: 1, panX: 0, panY: 0 });
  const zoomTargetRef = useRef({ zoom: 1, panX: 0, panY: 0 });
  const zoomStartTimeRef = useRef(0);
  const zoomFocalPointRef = useRef({ x: 0, y: 0 });

  // Track when the last pan/selection ended to guard against accidental sticky creation
  const lastPanOrSelectionEndRef = useRef(0);

  // Selection rectangle state (for both Partnr mode and normal mode)
  const [selectionRect, setSelectionRect] = useState<SelectionRect | null>(null);
  const [isDraggingSelection, setIsDraggingSelection] = useState(false);

  // Drag-to-create state (for tool-based item creation)
  const [creationDrag, setCreationDrag] = useState<{
    tool: Tool;
    startX: number; // screen coords
    startY: number;
    currentX: number;
    currentY: number;
    canvasStartX: number; // canvas coords
    canvasStartY: number;
  } | null>(null);
  const creationDragRef = useRef(creationDrag);
  creationDragRef.current = creationDrag;

  // Refs to avoid stale closures in rAF callbacks
  const panStartRef = useRef(panStart);
  panStartRef.current = panStart;
  const selectionRectRef = useRef(selectionRect);
  selectionRectRef.current = selectionRect;
  
  // Keyboard shortcuts help overlay
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);
  
  // Shared boards modal
  const [showSharedBoardsModal, setShowSharedBoardsModal] = useState(false);
  const { sharedBoards, refetch: refetchSharedBoards } = useSharedBoards();
  
  // (lastBoardNavAt is now tracked in canvasStore — set synchronously in navigateToBoard)

  // Mind Bubble state
  const [showMindBubble, setShowMindBubble] = useState(false);
  const [syncModalState, setSyncModalState] = useState<{
    isOpen: boolean;
    item: any;
    dropPosition: { x: number; y: number };
    sourceBoardName: string;
  } | null>(null);
  const {
    panX,
    panY,
    zoom,
    items,
    allItems,
    boards,
    activeTool,
    currentBoardId,
    isEditingItem,
    setPan,
    setZoom,
    createItem,
    createBoard,
    clearSelection,
    selectItem,
    selectedItemIds,
    isLoading,
    isDraggingFromToolbar,
    setDraggingFromToolbar,
    moveItemsToBoard,
    isGridVisible,
    shouldFitToView,
    clearShouldFitToView,
    rescueStrandedItems,
    isBoardTransitioning,
  } = useCanvasStore();

  const { 
    activeChats, 
    isPartnrMode, 
    partnrSelectedItemIds, 
    togglePartnrItemSelection 
  } = usePartnrStore();

  const {
    isArrowMode,
    isDrawing: isDrawingArrow,
    startDrawing,
    updateDrawing,
    finishDrawing,
    cancelDrawing,
    hoveredConnector,
    setSelectedArrow,
    deleteArrow,
    selectedArrowId,
  } = useArrowStore();

  const MAX_PARTNR_SELECTIONS = 4;

  // Initialize undo/redo (registers keyboard shortcuts)
  useUndoRedo();

  // lastBoardNavAt is stamped synchronously in canvasStore.navigateToBoard —
  // no useEffect needed (eliminates the post-paint delay that caused the race).

  // Mind Bubble: Handler for when an item is dropped from the bubble onto the canvas
  const handleMindBubbleDrop = useCallback(async (item: any, screenPosition: { x: number; y: number }) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect || !currentBoardId) return;
    
    // Convert screen position to canvas position
    const canvasPosition = {
      x: Math.round(((screenPosition.x - rect.left) / zoom - panX) / 20) * 20,
      y: Math.round(((screenPosition.y - rect.top) / zoom - panY) / 20) * 20,
    };
    
    // Check if item belongs to another board
    if (item.board_id && item.board_id !== currentBoardId) {
      // Show sync choice modal
      const sourceBoard = boards.find(b => b.id === item.board_id);
      setSyncModalState({
        isOpen: true,
        item,
        dropPosition: canvasPosition,
        sourceBoardName: sourceBoard?.name || 'another board',
      });
    } else {
      // Item is from Wall (no board) - move it to this board
      await moveItemsToBoard([item.id], currentBoardId);
      
      // Update position after move
      const { updateItem } = useCanvasStore.getState();
      await updateItem(item.id, {
        position_x: canvasPosition.x,
        position_y: canvasPosition.y,
      });
      
      toast.success('Item added to board');
      setShowMindBubble(false);
    }
  }, [currentBoardId, zoom, panX, panY, boards, moveItemsToBoard]);

  // Mind Bubble: Handler for duplicating an item
  const handleMindBubbleDuplicate = useCallback(async () => {
    if (!syncModalState || !currentBoardId) return;
    const { item, dropPosition } = syncModalState;
    
    // Create duplicate with new ID
    await createItem({
      board_id: currentBoardId,
      item_type: item.item_type,
      position_x: dropPosition.x,
      position_y: dropPosition.y,
      width: item.width,
      height: item.height,
      content: item.content,
    });
    
    toast.success('Item duplicated');
    setSyncModalState(null);
    setShowMindBubble(false);
  }, [syncModalState, currentBoardId, createItem]);

  // Mind Bubble: Handler for syncing an item
  const handleMindBubbleSync = useCallback(async () => {
    if (!syncModalState || !currentBoardId) return;
    const { item, dropPosition } = syncModalState;
    
    // Create synced_item reference
    await createItem({
      board_id: currentBoardId,
      item_type: 'synced_item' as any,
      position_x: dropPosition.x,
      position_y: dropPosition.y,
      width: item.width,
      height: item.height,
      content: {
        source_item_id: item.id,
        source_board_id: item.board_id,
      },
    });
    
    toast.success('Synced item created');
    setSyncModalState(null);
    setShowMindBubble(false);
  }, [syncModalState, currentBoardId, createItem]);

  // Mind Bubble: Show after board creation if user has pinned items
  const handleBoardCreatedShowMindBubble = useCallback(() => {
    const pinnedItems = allItems.filter(item => item.is_pinned_wall);
    if (pinnedItems.length > 0) {
      // Small delay to let the board creation animation complete
      setTimeout(() => {
        setShowMindBubble(true);
      }, 500);
    }
  }, [allItems]);

  useEffect(() => {
    const updateViewportSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setViewportSize({ width: rect.width, height: rect.height });
        // Also update the store so setPan can use viewport dimensions for dynamic boundaries
        useCanvasStore.getState().setViewportSize(rect.width, rect.height);
      }
    };
    
    updateViewportSize();
    window.addEventListener('resize', updateViewportSize);
    return () => window.removeEventListener('resize', updateViewportSize);
  }, []);

  // Track previous board ID to detect board changes
  const prevBoardIdRef = useRef<string | null>(null);
  const hasAutoFittedRef = useRef(false);

  // FIX 2: Disable browser swipe navigation (back/forward gestures)
  useEffect(() => {
    // Prevent horizontal scroll that triggers browser navigation
    const preventSwipeNavigation = (e: WheelEvent) => {
      // Prevent horizontal swipe gestures
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY) * 0.5) {
        e.preventDefault();
      }
    };
    
    const container = containerRef.current;
    container?.addEventListener('wheel', preventSwipeNavigation, { passive: false });
    
    // Set CSS to prevent overscroll behavior
    document.body.style.overscrollBehaviorX = 'none';
    
    return () => {
      container?.removeEventListener('wheel', preventSwipeNavigation);
      document.body.style.overscrollBehaviorX = 'auto';
    };
  }, []);

  // ─── Touch gesture handling ───────────────────────────────────────────
  // Single-finger: pan / flick / tap / double-tap / long-press
  // Two-finger: pinch-to-zoom (with simultaneous pan)
  // All gestures use native listeners (passive: false) so we can preventDefault
  // to stop the browser from doing its own scroll/zoom.

  const touchStateRef = useRef<{
    // Identifiers of active touches
    startTouches: { id: number; x: number; y: number }[];
    // Single-finger pan state
    isPanning: boolean;
    panStartX: number;
    panStartY: number;
    panStartPanX: number;
    panStartPanY: number;
    // Velocity tracking (for flick / kinetic scroll)
    lastMoveTime: number;
    lastMoveX: number;
    lastMoveY: number;
    // Pinch state
    isPinching: boolean;
    initialPinchDist: number;
    initialPinchZoom: number;
    initialPinchCenterX: number;
    initialPinchCenterY: number;
    initialPinchPanX: number;
    initialPinchPanY: number;
    // Tap detection
    tapTimeout: ReturnType<typeof setTimeout> | null;
    lastTapTime: number;
    lastTapX: number;
    lastTapY: number;
    // Long press
    longPressTimeout: ReturnType<typeof setTimeout> | null;
    hasMoved: boolean;
  }>({
    startTouches: [],
    isPanning: false,
    panStartX: 0, panStartY: 0,
    panStartPanX: 0, panStartPanY: 0,
    lastMoveTime: 0, lastMoveX: 0, lastMoveY: 0,
    isPinching: false,
    initialPinchDist: 0, initialPinchZoom: 1,
    initialPinchCenterX: 0, initialPinchCenterY: 0,
    initialPinchPanX: 0, initialPinchPanY: 0,
    tapTimeout: null, lastTapTime: 0, lastTapX: 0, lastTapY: 0,
    longPressTimeout: null, hasMoved: false,
  });

  // Animated zoom function - smoothly zooms toward focal point
  const animateZoomTo = useCallback((
    targetZoom: number,
    focalScreenX: number,
    focalScreenY: number
  ) => {
    // Cancel any existing zoom animation
    if (zoomAnimationRef.current) {
      cancelAnimationFrame(zoomAnimationRef.current);
    }

    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    // Get current values from store (might be mid-animation)
    const { zoom: currentZoom, panX: currentPanX, panY: currentPanY } = useCanvasStore.getState();

    // Calculate focal point in canvas space before zoom
    const focalCanvasX = (focalScreenX - rect.left) / currentZoom - currentPanX;
    const focalCanvasY = (focalScreenY - rect.top) / currentZoom - currentPanY;

    // Clamp target zoom with small overshoot allowance for bounce
    const clampedTarget = Math.max(
      ZOOM_MIN - ZOOM_BOUNCE_OVERSHOOT,
      Math.min(ZOOM_MAX + ZOOM_BOUNCE_OVERSHOOT, targetZoom)
    );

    // Calculate pan adjustment to keep focal point stationary
    const targetPanX = (focalScreenX - rect.left) / clampedTarget - focalCanvasX;
    const targetPanY = (focalScreenY - rect.top) / clampedTarget - focalCanvasY;

    // Store animation params
    zoomStartRef.current = { zoom: currentZoom, panX: currentPanX, panY: currentPanY };
    zoomTargetRef.current = { zoom: clampedTarget, panX: targetPanX, panY: targetPanY };
    zoomStartTimeRef.current = performance.now();
    zoomFocalPointRef.current = { x: focalScreenX, y: focalScreenY };

    const animate = () => {
      const elapsed = performance.now() - zoomStartTimeRef.current;
      const progress = Math.min(elapsed / ZOOM_ANIMATION_DURATION, 1);
      const easedProgress = easeOutCubic(progress);

      // Interpolate zoom and pan
      const newZoom = zoomStartRef.current.zoom +
        (zoomTargetRef.current.zoom - zoomStartRef.current.zoom) * easedProgress;
      const newPanX = zoomStartRef.current.panX +
        (zoomTargetRef.current.panX - zoomStartRef.current.panX) * easedProgress;
      const newPanY = zoomStartRef.current.panY +
        (zoomTargetRef.current.panY - zoomStartRef.current.panY) * easedProgress;

      // Apply values directly to store
      const store = useCanvasStore.getState();
      store.setZoom(newZoom);
      store.setPan(newPanX, newPanY);

      if (progress < 1) {
        zoomAnimationRef.current = requestAnimationFrame(animate);
      } else {
        zoomAnimationRef.current = null;

        // Bounce-back if exceeded limits
        if (newZoom < ZOOM_MIN || newZoom > ZOOM_MAX) {
          animateZoomTo(
            Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, newZoom)),
            zoomFocalPointRef.current.x,
            zoomFocalPointRef.current.y
          );
        }
      }
    };

    zoomAnimationRef.current = requestAnimationFrame(animate);
  }, []);

  // Inertia animation function
  const startInertiaAnimation = useCallback(() => {
    const animate = () => {
      const { panX: currentPanX, panY: currentPanY, setPan: setStorePan } = useCanvasStore.getState();
      const velocity = velocityRef.current;

      // Apply velocity to pan position
      const newPanX = currentPanX + velocity.x;
      const newPanY = currentPanY + velocity.y;
      setStorePan(newPanX, newPanY); // setPan clamps dynamically

      // Check if we hit a boundary (setPan clamped the value) — zero velocity in that direction
      const { panX: actualPanX, panY: actualPanY } = useCanvasStore.getState();
      if (velocity.x > 0 && actualPanX < newPanX) velocity.x = 0;
      if (velocity.x < 0 && actualPanX > newPanX) velocity.x = 0;
      if (velocity.y > 0 && actualPanY < newPanY) velocity.y = 0;
      if (velocity.y < 0 && actualPanY > newPanY) velocity.y = 0;

      // Apply friction to slow down
      velocity.x *= FRICTION;
      velocity.y *= FRICTION;

      // Continue animation if velocity is above threshold
      const speed = Math.sqrt(velocity.x * velocity.x + velocity.y * velocity.y);
      if (speed > MIN_VELOCITY) {
        inertiaAnimationRef.current = requestAnimationFrame(animate);
      } else {
        inertiaAnimationRef.current = null;
        velocityRef.current = { x: 0, y: 0 };
      }
    };

    // Only start if there's meaningful velocity
    const speed = Math.sqrt(velocityRef.current.x ** 2 + velocityRef.current.y ** 2);
    if (speed > MIN_VELOCITY) {
      inertiaAnimationRef.current = requestAnimationFrame(animate);
    }
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const LONG_PRESS_MS = 500;
    const TAP_DIST_THRESHOLD = 10; // px movement to cancel a tap
    const DOUBLE_TAP_MS = 300;

    // ── helpers ──
    const dist = (a: Touch, b: Touch) =>
      Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

    const midpoint = (a: Touch, b: Touch) => ({
      x: (a.clientX + b.clientX) / 2,
      y: (a.clientY + b.clientY) / 2,
    });

    const clearLongPress = () => {
      const ts = touchStateRef.current;
      if (ts.longPressTimeout) { clearTimeout(ts.longPressTimeout); ts.longPressTimeout = null; }
    };

    // ── touchstart ──
    const onTouchStart = (e: TouchEvent) => {
      const ts = touchStateRef.current;

      // Cancel any ongoing inertia
      if (inertiaAnimationRef.current) {
        cancelAnimationFrame(inertiaAnimationRef.current);
        inertiaAnimationRef.current = null;
      }

      ts.startTouches = Array.from(e.touches).map(t => ({ id: t.identifier, x: t.clientX, y: t.clientY }));
      ts.hasMoved = false;

      if (e.touches.length === 1) {
        // ─ Single finger ─
        const touch = e.touches[0];
        const target = touch.target as Element;
        const onItem = !!target?.closest?.('[data-canvas-item]');

        if (onItem) {
          // Let CanvasItem handle its own touch drag — don't pan
          return;
        }

        e.preventDefault(); // prevent browser scroll

        const store = useCanvasStore.getState();
        ts.isPanning = true;
        ts.panStartX = touch.clientX;
        ts.panStartY = touch.clientY;
        ts.panStartPanX = store.panX;
        ts.panStartPanY = store.panY;
        ts.lastMoveTime = performance.now();
        ts.lastMoveX = touch.clientX;
        ts.lastMoveY = touch.clientY;
        velocityRef.current = { x: 0, y: 0 };

        // Long press timer
        clearLongPress();
        ts.longPressTimeout = setTimeout(() => {
          if (!ts.hasMoved) {
            // Trigger context menu at touch position
            const rect = container.getBoundingClientRect();
            const canvasX = (touch.clientX - rect.left) / store.zoom - store.panX;
            const canvasY = (touch.clientY - rect.top) / store.zoom - store.panY;
            container.dispatchEvent(new MouseEvent('contextmenu', {
              clientX: touch.clientX, clientY: touch.clientY,
              bubbles: true, cancelable: true,
            }));
            ts.isPanning = false; // cancel pan if long-pressed
          }
        }, LONG_PRESS_MS);

      } else if (e.touches.length === 2) {
        // ─ Two fingers — start pinch-to-zoom ─
        e.preventDefault();
        clearLongPress();
        ts.isPanning = false;

        const t0 = e.touches[0];
        const t1 = e.touches[1];
        const store = useCanvasStore.getState();

        ts.isPinching = true;
        ts.initialPinchDist = dist(t0, t1);
        ts.initialPinchZoom = store.zoom;
        const mid = midpoint(t0, t1);
        ts.initialPinchCenterX = mid.x;
        ts.initialPinchCenterY = mid.y;
        ts.initialPinchPanX = store.panX;
        ts.initialPinchPanY = store.panY;
      }
    };

    // ── touchmove ──
    const onTouchMove = (e: TouchEvent) => {
      const ts = touchStateRef.current;

      if (ts.isPinching && e.touches.length >= 2) {
        // ─ Pinch-to-zoom + simultaneous two-finger pan ─
        e.preventDefault();

        const t0 = e.touches[0];
        const t1 = e.touches[1];
        const currentDist = dist(t0, t1);
        const scale = currentDist / ts.initialPinchDist;
        const newZoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, ts.initialPinchZoom * scale));

        const mid = midpoint(t0, t1);
        const rect = container.getBoundingClientRect();

        // Focal point in canvas coordinates (at initial zoom/pan)
        const focalCanvasX = (ts.initialPinchCenterX - rect.left) / ts.initialPinchZoom - ts.initialPinchPanX;
        const focalCanvasY = (ts.initialPinchCenterY - rect.top) / ts.initialPinchZoom - ts.initialPinchPanY;

        // New pan keeps the original focal canvas point under the current midpoint
        const newPanX = (mid.x - rect.left) / newZoom - focalCanvasX;
        const newPanY = (mid.y - rect.top) / newZoom - focalCanvasY;

        const store = useCanvasStore.getState();
        store.setZoom(newZoom);
        store.setPan(newPanX, newPanY);

        ts.hasMoved = true;
        return;
      }

      if (ts.isPanning && e.touches.length === 1) {
        // ─ Single-finger pan ─
        e.preventDefault();
        const touch = e.touches[0];
        const store = useCanvasStore.getState();

        const dx = touch.clientX - ts.panStartX;
        const dy = touch.clientY - ts.panStartY;

        // Track if finger moved enough to disqualify tap/long-press
        if (Math.abs(dx) > TAP_DIST_THRESHOLD || Math.abs(dy) > TAP_DIST_THRESHOLD) {
          ts.hasMoved = true;
          clearLongPress();
        }

        const newPanX = ts.panStartPanX + dx / store.zoom;
        const newPanY = ts.panStartPanY + dy / store.zoom;
        store.setPan(newPanX, newPanY);

        // Velocity tracking for flick/inertia
        const now = performance.now();
        const dt = now - ts.lastMoveTime;
        if (dt > 0) {
          const vx = (touch.clientX - ts.lastMoveX) / dt;
          const vy = (touch.clientY - ts.lastMoveY) / dt;
          velocityRef.current = {
            x: velocityRef.current.x * 0.5 + vx * VELOCITY_SCALE * 0.5,
            y: velocityRef.current.y * 0.5 + vy * VELOCITY_SCALE * 0.5,
          };
        }
        ts.lastMoveTime = now;
        ts.lastMoveX = touch.clientX;
        ts.lastMoveY = touch.clientY;
      }
    };

    // ── touchend ──
    const onTouchEnd = (e: TouchEvent) => {
      const ts = touchStateRef.current;
      clearLongPress();

      if (ts.isPinching) {
        // End pinch — if one finger remains, start panning from that finger
        if (e.touches.length === 1) {
          ts.isPinching = false;
          const touch = e.touches[0];
          const store = useCanvasStore.getState();
          ts.isPanning = true;
          ts.panStartX = touch.clientX;
          ts.panStartY = touch.clientY;
          ts.panStartPanX = store.panX;
          ts.panStartPanY = store.panY;
          ts.lastMoveTime = performance.now();
          ts.lastMoveX = touch.clientX;
          ts.lastMoveY = touch.clientY;
          velocityRef.current = { x: 0, y: 0 };
        } else if (e.touches.length === 0) {
          ts.isPinching = false;
        }
        return;
      }

      if (e.touches.length === 0 && ts.isPanning) {
        ts.isPanning = false;

        // Flick / kinetic scroll
        startInertiaAnimation();

        // Tap detection (only if finger barely moved)
        if (!ts.hasMoved) {
          const now = Date.now();
          const touch = e.changedTouches[0];

          // Double-tap detection
          const dtSinceLast = now - ts.lastTapTime;
          const tapDist = Math.hypot(touch.clientX - ts.lastTapX, touch.clientY - ts.lastTapY);

          if (dtSinceLast < DOUBLE_TAP_MS && tapDist < 30) {
            // ── Double tap → smart zoom (toggle between 1x and 2x) ──
            if (ts.tapTimeout) { clearTimeout(ts.tapTimeout); ts.tapTimeout = null; }
            const store = useCanvasStore.getState();
            const targetZoom = store.zoom < 1.5 ? 2 : 1;
            animateZoomTo(targetZoom, touch.clientX, touch.clientY);
            ts.lastTapTime = 0;
          } else {
            // ── Single tap (delayed to allow double-tap) ──
            ts.lastTapTime = now;
            ts.lastTapX = touch.clientX;
            ts.lastTapY = touch.clientY;

            if (ts.tapTimeout) clearTimeout(ts.tapTimeout);
            ts.tapTimeout = setTimeout(() => {
              // Deselect / tap on empty canvas
              const store = useCanvasStore.getState();
              if (store.activeTool === 'select') {
                store.clearSelection();
              }
              ts.tapTimeout = null;
            }, DOUBLE_TAP_MS);
          }
        }
      }
    };

    const onTouchCancel = () => {
      const ts = touchStateRef.current;
      clearLongPress();
      ts.isPanning = false;
      ts.isPinching = false;
    };

    container.addEventListener('touchstart', onTouchStart, { passive: false });
    container.addEventListener('touchmove', onTouchMove, { passive: false });
    container.addEventListener('touchend', onTouchEnd, { passive: false });
    container.addEventListener('touchcancel', onTouchCancel, { passive: false });

    return () => {
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchmove', onTouchMove);
      container.removeEventListener('touchend', onTouchEnd);
      container.removeEventListener('touchcancel', onTouchCancel);
      clearLongPress();
      const ts = touchStateRef.current;
      if (ts.tapTimeout) clearTimeout(ts.tapTimeout);
    };
  }, [animateZoomTo, startInertiaAnimation]);

  // Viewport culling disabled — render all items.
  // With typical board sizes (~50-300 items) the overhead is negligible,
  // and culling was causing items to vanish mid-drag when position updates
  // temporarily moved them outside the buffer zone.
  const visibleItems = items;

  // LOD tier: controls rendering detail based on zoom and visible item count
  // Tier 1 (full): zoom >= 25% → full content render
  // Tier 2 (simplified): zoom 15-24% → title-only cards
  // Tier 3 (dot): zoom < 15% or > 200 visible → colored rectangles, no text
  const lodTier: 1 | 2 | 3 = useMemo(() => {
    const count = visibleItems.length;
    if (zoom < 0.15 || count > 200) return 3;
    if (zoom < 0.25 || count > 100) return 2;
    return 1;
  }, [zoom, visibleItems.length]);

  // Check if a point is within an item's bounds
  const isPointInItem = useCallback((px: number, py: number, item: { position_x: number; position_y: number; width: number; height: number }) => {
    return px >= item.position_x && px <= item.position_x + item.width &&
           py >= item.position_y && py <= item.position_y + item.height;
  }, []);

  // Find board card at coordinates (for coordinate-based drop detection)
  const findBoardCardAtPoint = useCallback((canvasX: number, canvasY: number, draggedItemIds: string[]): string | null => {
    // Find all board_link items that are NOT being dragged
    const boardCards = items.filter(item => 
      item.item_type === 'board_link' && 
      !draggedItemIds.includes(item.id)
    );
    
    // Check if point is inside any board card (prioritize topmost = highest z_index)
    const sortedByZ = [...boardCards].sort((a, b) => b.z_index - a.z_index);
    
    for (const card of sortedByZ) {
      if (isPointInItem(canvasX, canvasY, card)) {
        const content = card.content as { linked_board_id?: string };
        if (content.linked_board_id) {
          return content.linked_board_id;
        }
      }
    }
    
    return null;
  }, [items, isPointInItem]);

  // Check if item intersects with selection rectangle
  const itemIntersectsRect = useCallback((item: { position_x: number; position_y: number; width: number; height: number }, rect: SelectionRect) => {
    // Convert screen coordinates to canvas coordinates
    const rectLeft = Math.min(rect.startX, rect.currentX) / zoom - panX;
    const rectRight = Math.max(rect.startX, rect.currentX) / zoom - panX;
    const rectTop = Math.min(rect.startY, rect.currentY) / zoom - panY;
    const rectBottom = Math.max(rect.startY, rect.currentY) / zoom - panY;

    const itemRight = item.position_x + item.width;
    const itemBottom = item.position_y + item.height;

    return !(item.position_x > rectRight || 
             itemRight < rectLeft || 
             item.position_y > rectBottom || 
             itemBottom < rectTop);
  }, [zoom, panX, panY]);

  // Handle selection rectangle mouse up - for Partnr mode
  const handlePartnrSelectionEnd = useCallback(() => {
    if (!selectionRect || !isDraggingSelection || !isPartnrMode) {
      return;
    }

    // Find items that intersect with the selection rectangle
    const selectableItems = items
      .filter(item => item.item_type !== 'board_link')
      .filter(item => itemIntersectsRect(item, selectionRect))
      .filter(item => !partnrSelectedItemIds.has(item.id));

    // Sort by position (top-left to bottom-right)
    selectableItems.sort((a, b) => {
      const distA = a.position_y * 10000 + a.position_x;
      const distB = b.position_y * 10000 + b.position_x;
      return distA - distB;
    });

    // Calculate how many more we can select
    const currentCount = partnrSelectedItemIds.size;
    const availableSlots = MAX_PARTNR_SELECTIONS - currentCount;

    if (availableSlots === 0) {
      toast.error(`Maximum ${MAX_PARTNR_SELECTIONS} items can be referenced`);
    } else if (selectableItems.length > availableSlots) {
      // Select only first N items
      for (let i = 0; i < availableSlots; i++) {
        togglePartnrItemSelection(selectableItems[i].id);
      }
      toast.info(`Maximum ${MAX_PARTNR_SELECTIONS} items selected`);
    } else {
      // Select all items in the rectangle
      for (const item of selectableItems) {
        togglePartnrItemSelection(item.id);
      }
    }
  }, [selectionRect, isDraggingSelection, isPartnrMode, items, partnrSelectedItemIds, togglePartnrItemSelection, itemIntersectsRect]);

  // Handle selection rectangle mouse up - for normal mode (multi-select)
  const handleNormalSelectionEnd = useCallback(() => {
    if (!selectionRect || !isDraggingSelection || isPartnrMode) {
      return;
    }

    // Find items that intersect with the selection rectangle
    const selectableItems = items
      .filter(item => itemIntersectsRect(item, selectionRect));

    // Select all items in the rectangle (no limit in normal mode)
    if (selectableItems.length > 0) {
      for (const item of selectableItems) {
        selectItem(item.id, true); // additive selection
      }
    } else {
      // If the selection rectangle was very small (a click on empty space),
      // we already clear selection on mousedown
    }
  }, [selectionRect, isDraggingSelection, isPartnrMode, items, selectItem, itemIntersectsRect]);

  // Selection clearing is handled in handleMouseDown when starting a selection rectangle on empty canvas

  // Handle wheel for zooming with smooth animation (FIX 5 & 6: reduced sensitivity, zoom toward center)
  const handleWheel = useCallback((e: React.WheelEvent) => {
    // Only block canvas pan/zoom if the wheel event originates from within a
    // scrollable editor (i.e., the text card's content area). This ensures
    // two-finger trackpad pan/zoom still works when an item is merely selected.
    if (isEditingItem) {
      const target = e.target as HTMLElement;
      const scrollableEditor = target.closest('.overflow-y-auto, textarea');
      if (scrollableEditor) {
        // Let the editor handle its own scrolling — don't pan the canvas
        return;
      }
      // If the wheel didn't originate from a scrollable editor, let the canvas handle it
    }

    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();

      // Cancel any running zoom animation so we take over directly
      if (zoomAnimationRef.current) {
        cancelAnimationFrame(zoomAnimationRef.current);
        zoomAnimationRef.current = null;
      }

      const { zoom: currentZoom, panX: currentPanX, panY: currentPanY } = useCanvasStore.getState();

      // Proportional zoom step — capped for control
      const rawStep = -e.deltaY * ZOOM_STEP_SCALE;
      const step = Math.sign(rawStep) * Math.min(Math.abs(rawStep), ZOOM_MAX_STEP);
      const newZoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, currentZoom * (1 + step)));

      // Keep the point under the cursor stationary (focal-point zoom)
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        const focalCanvasX = (e.clientX - rect.left) / currentZoom - currentPanX;
        const focalCanvasY = (e.clientY - rect.top) / currentZoom - currentPanY;
        const newPanX = (e.clientX - rect.left) / newZoom - focalCanvasX;
        const newPanY = (e.clientY - rect.top) / newZoom - focalCanvasY;
        useCanvasStore.getState().setZoom(newZoom);
        useCanvasStore.getState().setPan(newPanX, newPanY);
      } else {
        useCanvasStore.getState().setZoom(newZoom);
      }
    } else {
      // Pan with scroll wheel - prevent horizontal swipe navigation (FIX 2)
      e.preventDefault();
      // Weighted dampening for heavy, controlled pan feel
      const PAN_DAMPENING = 0.35;
      setPan(
        panX - (e.deltaX * PAN_DAMPENING) / zoom,
        panY - (e.deltaY * PAN_DAMPENING) / zoom
      );
    }
  }, [isEditingItem, zoom, panX, panY, setPan]);

  // Check if user is typing in a text input
  const isEditingText = useCallback(() => {
    const activeElement = document.activeElement;
    if (!activeElement) return false;
    
    const tagName = activeElement.tagName.toLowerCase();
    if (tagName === 'input' || tagName === 'textarea') return true;
    if (activeElement.getAttribute('contenteditable') === 'true') return true;
    
    // Check if inside Partnr chat window
    if (activeElement.closest('.partnr-chat-window')) return true;
    
    return false;
  }, []);

  // Get center of viewport in canvas coordinates
  const getViewportCenter = useCallback(() => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return { x: 100, y: 100 };
    
    return {
      x: (rect.width / 2) / zoom - panX,
      y: (rect.height / 2) / zoom - panY
    };
  }, [zoom, panX, panY]);

  // Convert screen position to canvas coordinates
  const screenToCanvas = useCallback((screenX: number, screenY: number) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    
    return {
      x: (screenX - rect.left) / zoom - panX,
      y: (screenY - rect.top) / zoom - panY
    };
  }, [zoom, panX, panY]);

  // Handle paste functionality
  const handlePaste = useCallback(async (position: { x: number; y: number }) => {
    if (!currentBoardId) return;

    try {
      // Try to read clipboard content
      const clipboardItems = await navigator.clipboard.read();
      
      for (const clipboardItem of clipboardItems) {
        // Check for images first
        for (const type of clipboardItem.types) {
          if (type.startsWith('image/')) {
            const blob = await clipboardItem.getType(type);
            const file = new File([blob], 'pasted-image.png', { type });
            
            // Upload image - would need to integrate with DropZone logic
            toast.info('Image paste - use drag & drop for images');
            return;
          }
        }
      }

      // Fall back to text
      const text = await navigator.clipboard.readText();
      if (!text) return;

      // Snap position to grid
      const snappedX = Math.round(position.x / 20) * 20;
      const snappedY = Math.round(position.y / 20) * 20;

      // Check if it's a URL
      const urlRegex = /^(https?:\/\/[^\s]+)$/;
      if (urlRegex.test(text.trim())) {
        const linkUrl = text.trim();
        toast.info('Fetching link preview...');
        
        // Fetch metadata from edge function
        try {
          const { data: metadata, error } = await supabase.functions.invoke('fetch-link-metadata', {
            body: { url: linkUrl }
          });
          
          if (error) {
            console.error('Metadata fetch error:', error);
          }
          
          await createItem({
            board_id: currentBoardId,
            item_type: 'link',
            position_x: snappedX,
            position_y: snappedY,
            width: 380,
            height: metadata?.image ? 280 : 120,
            content: {
              url: linkUrl,
              title: metadata?.title || '',
              description: metadata?.description || '',
              image: metadata?.image || '',
              siteName: metadata?.siteName || '',
              favicon: metadata?.favicon || '',
              imageAspectRatio: metadata?.imageAspectRatio,
              contentType: metadata?.contentType || 'website',
              price: metadata?.price || null,
              screenshot: metadata?.screenshot || null,
            },
          });
          toast.success('Link added to canvas');
        } catch (err) {
          console.error('Link metadata error:', err);
          // Create link without metadata as fallback
          await createItem({
            board_id: currentBoardId,
            item_type: 'link',
            position_x: snappedX,
            position_y: snappedY,
            width: 380,
            height: 120,
            content: { url: linkUrl, title: '', description: '', image: '' },
          });
          toast.success('Link pasted to canvas');
        }
        return;
      }

      // Check if content is markdown
      if (isMarkdownContent(text)) {
        const htmlContent = markdownToHtml(text);
        await createItem({
          board_id: currentBoardId,
          item_type: 'text_card',
          position_x: snappedX,
          position_y: snappedY,
          width: 320,
          height: 400,
          content: { body: htmlContent },
        });
        toast.success('Markdown pasted to canvas');
        return;
      }

      // Create sticky note for short text, text card for longer
      if (text.length < 200) {
        await createItem({
          board_id: currentBoardId,
          item_type: 'sticky',
          position_x: snappedX,
          position_y: snappedY,
          width: 200,
          height: 150,
          content: { text, color: 'yellow' },
        });
        toast.success('Note pasted to canvas');
      } else {
        await createItem({
          board_id: currentBoardId,
          item_type: 'text_card',
          position_x: snappedX,
          position_y: snappedY,
          width: 300,
          height: 200,
          content: { body: text },
        });
        toast.success('Text card pasted to canvas');
      }
    } catch (error) {
      console.error('Paste error:', error);
      toast.error('Could not paste content');
    }
  }, [currentBoardId, createItem]);

  // Select all items on current board
  const handleSelectAll = useCallback(() => {
    if (isPartnrMode) return;
    
    const allItemIds = items.map(item => item.id);
    allItemIds.forEach(id => selectItem(id, true));
    toast.info(`Selected ${allItemIds.length} items`);
  }, [items, selectItem, isPartnrMode]);

  // Fit animation refs
  const fitAnimationRef = useRef<number | null>(null);
  
  // Animate pan/zoom to target values (for fit-to-view)
  const animateFitTo = useCallback((targetPanX: number, targetPanY: number, targetZoom: number, rect: DOMRect) => {
    // Cancel any existing fit animation
    if (fitAnimationRef.current) {
      cancelAnimationFrame(fitAnimationRef.current);
    }
    // Cancel zoom animation too
    if (zoomAnimationRef.current) {
      cancelAnimationFrame(zoomAnimationRef.current);
    }

    const { zoom: startZoom, panX: startPanX, panY: startPanY } = useCanvasStore.getState();
    const startTime = performance.now();

    const animate = () => {
      const elapsed = performance.now() - startTime;
      const progress = Math.min(elapsed / FIT_ANIMATION_DURATION, 1);
      const easedProgress = easeOutCubic(progress);

      // Interpolate all values
      const newZoom = startZoom + (targetZoom - startZoom) * easedProgress;
      const newPanX = startPanX + (targetPanX - startPanX) * easedProgress;
      const newPanY = startPanY + (targetPanY - startPanY) * easedProgress;

      const store = useCanvasStore.getState();
      store.setZoom(newZoom);
      store.setPan(newPanX, newPanY);

      if (progress < 1) {
        fitAnimationRef.current = requestAnimationFrame(animate);
      } else {
        fitAnimationRef.current = null;
      }
    };

    fitAnimationRef.current = requestAnimationFrame(animate);
  }, []);

  // Animated fit all items in view with smooth transition
  // Accounts for fixed navbar and sidebar heights so items don't appear behind UI overlays
  const fitAllItemsInView = useCallback(() => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const { getBoundingBox } = useCanvasStore.getState();
    const bounds = getBoundingBox();

    if (!bounds) {
      // No items - animate to origin at 100% zoom
      animateFitTo(0, 0, 1, rect);
      toast.success('Canvas reset');
      return;
    }

    // Ensure bounds respect minimum item Y (items shouldn't be behind navbar)
    const safeBounds = {
      ...bounds,
      minY: Math.max(bounds.minY, MIN_ITEM_Y),
      // Recalculate center if minY was adjusted
      centerY: bounds.minY < MIN_ITEM_Y 
        ? (MIN_ITEM_Y + bounds.maxY) / 2 
        : bounds.centerY,
    };

    // Calculate USABLE viewport area (accounting for UI overlays)
    const usableWidth = rect.width - FIT_PADDING_LEFT - FIT_PADDING_RIGHT;
    const usableHeight = rect.height - FIT_PADDING_TOP - FIT_PADDING_BOTTOM;

    // Calculate zoom to fit content in usable area
    const scaleX = usableWidth / safeBounds.width;
    const scaleY = usableHeight / (safeBounds.maxY - safeBounds.minY);
    const targetZoom = Math.min(Math.max(Math.min(scaleX, scaleY), ZOOM_MIN), 2); // Clamp between ZOOM_MIN and 200%

    // Calculate pan to center content in usable area
    // Usable area center is offset from viewport center
    const usableCenterX = FIT_PADDING_LEFT + (usableWidth / 2);
    const usableCenterY = FIT_PADDING_TOP + (usableHeight / 2);
    
    const targetPanX = (usableCenterX / targetZoom) - safeBounds.centerX;
    const targetPanY = (usableCenterY / targetZoom) - safeBounds.centerY;

    animateFitTo(targetPanX, targetPanY, targetZoom, rect);
  }, [animateFitTo]);

  // Auto fit-to-view when navigating to a new board - uses polling for reliability
  useEffect(() => {
    if (!currentBoardId) return;
    if (!shouldFitToView) return;

    // Cancel any previous fit animation
    if (fitAnimationRef.current) {
      cancelAnimationFrame(fitAnimationRef.current);
      fitAnimationRef.current = null;
    }

    // Poll for items to be loaded instead of using a fixed delay
    let attempts = 0;
    const maxAttempts = 20; // 20 * 50ms = 1 second max wait
    let pollTimer: ReturnType<typeof setTimeout>;

    const tryFit = () => {
      attempts++;
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) {
        if (attempts < maxAttempts) {
          pollTimer = setTimeout(tryFit, 50);
        }
        return;
      }

      const state = useCanvasStore.getState();
      const boardItems = state.items.filter(i => i.board_id === currentBoardId);

      // If items haven't loaded yet and we haven't exceeded max attempts, keep polling
      if (boardItems.length === 0 && attempts < maxAttempts) {
        pollTimer = setTimeout(tryFit, 50);
        return;
      }

      // Calculate fit target
      const { getBoundingBox } = state;
      const bounds = getBoundingBox();

      if (!bounds) {
        // Empty board — set to default view with smooth animation
        animateFitTo(0, 0, 1, rect);
        clearShouldFitToView();
        return;
      }

      // Account for UI overlays (same as fitAllItemsInView)
      const safeBounds = {
        ...bounds,
        minY: Math.max(bounds.minY, MIN_ITEM_Y),
        centerY: bounds.minY < MIN_ITEM_Y
          ? (MIN_ITEM_Y + bounds.maxY) / 2
          : bounds.centerY,
      };

      const usableWidth = rect.width - FIT_PADDING_LEFT - FIT_PADDING_RIGHT;
      const usableHeight = rect.height - FIT_PADDING_TOP - FIT_PADDING_BOTTOM;

      const scaleX = usableWidth / safeBounds.width;
      const scaleY = usableHeight / (safeBounds.maxY - safeBounds.minY);
      const targetZoom = Math.min(Math.max(Math.min(scaleX, scaleY), ZOOM_MIN), 2);

      const usableCenterX = FIT_PADDING_LEFT + usableWidth / 2;
      const usableCenterY = FIT_PADDING_TOP + usableHeight / 2;

      const targetPanX = (usableCenterX / targetZoom) - safeBounds.centerX;
      const targetPanY = (usableCenterY / targetZoom) - safeBounds.centerY;

      animateFitTo(targetPanX, targetPanY, targetZoom, rect);
      clearShouldFitToView();
    };

    // Start polling after a brief initial delay for React to render
    pollTimer = setTimeout(tryFit, 50);

    return () => {
      clearTimeout(pollTimer);
    };
  }, [currentBoardId, shouldFitToView, clearShouldFitToView, animateFitTo]);

  // Handle keyboard for space panning, delete, tool shortcuts, zoom, pan, etc.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't intercept keyboard events when user is typing in a text field
      if (isEditingText()) return;
      
      // ? key: Show shortcuts help
      if (e.key === '?' || (e.shiftKey && e.code === 'Slash')) {
        e.preventDefault();
        setShowShortcutsHelp(true);
        return;
      }
      
      // Cmd/Ctrl + A: Select all items
      if ((e.metaKey || e.ctrlKey) && e.key === 'a') {
        e.preventDefault();
        handleSelectAll();
        return;
      }

      // Cmd/Ctrl + C: Copy items (or content if single text item)
      if ((e.metaKey || e.ctrlKey) && e.key === 'c' && !isEditingText()) {
        const { selectedItemIds, items, copySelectedItems } = useCanvasStore.getState();
        if (selectedItemIds.size > 0) {
          e.preventDefault();
          
          // If single text-based item, copy text content to system clipboard too
          if (selectedItemIds.size === 1) {
            const selectedId = [...selectedItemIds][0];
            const selectedItem = items.find(i => i.id === selectedId);
            if (selectedItem && ['sticky', 'text_card', 'image'].includes(selectedItem.item_type)) {
              const content = selectedItem.content as Record<string, any>;
              let textToCopy = '';
              
              if (selectedItem.item_type === 'sticky') {
                textToCopy = content.text || '';
              } else if (selectedItem.item_type === 'text_card') {
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = content.body || '';
                const plainBody = tempDiv.textContent || tempDiv.innerText || '';
                textToCopy = content.title ? `${content.title}\n\n${plainBody}` : plainBody;
              } else if (selectedItem.item_type === 'image') {
                textToCopy = content.url || '';
              }
              
              if (textToCopy) {
                navigator.clipboard.writeText(textToCopy);
              }
            }
          }
          
          // Always copy items to internal clipboard for pasting
          copySelectedItems();
        }
        return;
      }

      // Cmd/Ctrl + V: Paste items or clipboard content
      if ((e.metaKey || e.ctrlKey) && e.key === 'v') {
        e.preventDefault();
        const position = getViewportCenter();
        
        // Check for copied items first
        const { hasCopiedItems, pasteItems } = useCanvasStore.getState();
        if (hasCopiedItems()) {
          pasteItems(position);
        } else {
          // Fall back to clipboard paste
          handlePaste(position);
        }
        return;
      }

      // Cmd/Ctrl + Shift + R: Rescue stranded items
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'r') {
        e.preventDefault();
        const count = rescueStrandedItems();
        if (count > 0) {
          toast.success(`Rescued ${count} hidden item${count > 1 ? 's' : ''}`);
        } else {
          toast.info('No hidden items found');
        }
        return;
      }
      
      if (e.code === 'Space' && !isSpacePressed) {
        e.preventDefault();
        setIsSpacePressed(true);
      }
      
      if (e.key === 'Escape') {
        // Cancel arrow drawing or exit arrow mode
        const arrowState = useArrowStore.getState();
        if (arrowState.isDrawing) {
          arrowState.cancelDrawing();
          return;
        }
        if (arrowState.isArrowMode) {
          arrowState.setArrowMode(false);
          useCanvasStore.getState().setActiveTool('select');
          return;
        }
        if (arrowState.selectedArrowId) {
          arrowState.setSelectedArrow(null);
          return;
        }
        
        clearSelection();
        const { clearPartnrSelection } = usePartnrStore.getState();
        clearPartnrSelection();
        return;
      }

      if (e.code === 'Delete' || e.code === 'Backspace') {
        // Use the store's isEditingItem flag (set by TipTap/sticky editors)
        // to distinguish "editing text" from "item selected but not editing".
        // isEditingText() checks the DOM for input/textarea/contenteditable —
        // but it's too broad and can return true when focus lingers on a
        // deselected editor. isEditingItem is authoritative.
        const { selectedItemIds, deleteItems, isEditingItem: isEditing } = useCanvasStore.getState();
        const { selectedArrowId, deleteArrow: deleteSelectedArrow, deleteArrowsByItem } = useArrowStore.getState();

        // If user is actively editing text inside an item, let backspace delete characters
        if (isEditing) return;

        // Delete selected arrow first
        if (selectedArrowId) {
          e.preventDefault();
          deleteSelectedArrow(selectedArrowId);
          return;
        }

        // Then delete selected items and their connected arrows
        if (selectedItemIds.size > 0) {
          e.preventDefault();

          // Capture full item snapshots BEFORE deletion for undo
          const { items: currentItems } = useCanvasStore.getState();
          const deletedItems = currentItems.filter(i => selectedItemIds.has(i.id));
          if (deletedItems.length > 0) {
            useHistoryStore.getState().pushAction({
              type: deletedItems.length > 1 ? 'DELETE_ITEMS' : 'DELETE_ITEM',
              timestamp: Date.now(),
              items: deletedItems,
            });
          }

          // Clean up arrows connected to deleted items
          for (const itemId of selectedItemIds) {
            deleteArrowsByItem(itemId);
          }
          deleteItems([...selectedItemIds]);
        }
      }
      
      // Shortcuts without modifiers
      if (!e.metaKey && !e.ctrlKey) {
        const store = useCanvasStore.getState();
        
        // Zoom shortcuts
        if (e.key === '+' || e.key === '=') {
          e.preventDefault();
          const rect = containerRef.current?.getBoundingClientRect();
          if (rect) {
            animateZoomTo(store.zoom * 1.25, rect.left + rect.width / 2, rect.top + rect.height / 2);
          }
          return;
        }
        
        if (e.key === '-') {
          e.preventDefault();
          const rect = containerRef.current?.getBoundingClientRect();
          if (rect) {
            animateZoomTo(store.zoom * 0.8, rect.left + rect.width / 2, rect.top + rect.height / 2);
          }
          return;
        }
        
        if (e.key === '0') {
          e.preventDefault();
          const rect = containerRef.current?.getBoundingClientRect();
          if (rect) {
            animateZoomTo(1, rect.left + rect.width / 2, rect.top + rect.height / 2);
          }
          toast.success('Zoom: 100%');
          return;
        }
        
        if (e.key === '1') {
          e.preventDefault();
          fitAllItemsInView();
          return;
        }
        
        
        // Arrow key handling - FIX 3: Move items if selected, otherwise pan canvas
        const hasSelectedItems = store.selectedItemIds.size > 0;
        const moveSpeed = e.shiftKey ? ARROW_MOVE_SPEED_FAST : ARROW_MOVE_SPEED;
        const panSpeed = e.shiftKey ? ARROW_PAN_SPEED_FAST : ARROW_PAN_SPEED;
        
        switch (e.code) {
          case 'ArrowUp':
            e.preventDefault();
            if (hasSelectedItems) {
              // Move selected items up
              store.selectedItemIds.forEach(itemId => {
                const item = store.items.find(i => i.id === itemId);
                if (item) {
                  store.updateItem(itemId, { position_y: item.position_y - moveSpeed });
                }
              });
            } else {
              setPan(store.panX, store.panY + panSpeed / store.zoom);
            }
            return;
          case 'ArrowDown':
            e.preventDefault();
            if (hasSelectedItems) {
              store.selectedItemIds.forEach(itemId => {
                const item = store.items.find(i => i.id === itemId);
                if (item) {
                  store.updateItem(itemId, { position_y: item.position_y + moveSpeed });
                }
              });
            } else {
              setPan(store.panX, store.panY - panSpeed / store.zoom);
            }
            return;
          case 'ArrowLeft':
            e.preventDefault();
            if (hasSelectedItems) {
              store.selectedItemIds.forEach(itemId => {
                const item = store.items.find(i => i.id === itemId);
                if (item) {
                  store.updateItem(itemId, { position_x: item.position_x - moveSpeed });
                }
              });
            } else {
              setPan(store.panX + panSpeed / store.zoom, store.panY);
            }
            return;
          case 'ArrowRight':
            e.preventDefault();
            if (hasSelectedItems) {
              store.selectedItemIds.forEach(itemId => {
                const item = store.items.find(i => i.id === itemId);
                if (item) {
                  store.updateItem(itemId, { position_x: item.position_x + moveSpeed });
                }
              });
            } else {
              setPan(store.panX - panSpeed / store.zoom, store.panY);
            }
            return;
        }
        
        // Tool shortcuts
        switch (e.code) {
          case 'KeyV':
            store.setActiveTool('select');
            useArrowStore.getState().setArrowMode(false);
            break;
          case 'KeyH':
            store.setActiveTool('pan');
            useArrowStore.getState().setArrowMode(false);
            break;
          case 'KeyB':
            store.setActiveTool('board');
            useArrowStore.getState().setArrowMode(false);
            break;
          case 'KeyS':
            store.setActiveTool('sticky');
            useArrowStore.getState().setArrowMode(false);
            break;
          case 'KeyT':
            store.setActiveTool('text_card');
            useArrowStore.getState().setArrowMode(false);
            break;
          case 'KeyD':
            store.setActiveTool('document');
            useArrowStore.getState().setArrowMode(false);
            break;
          case 'KeyL':
            store.setActiveTool('link');
            useArrowStore.getState().setArrowMode(false);
            break;
          case 'KeyA':
            const { isArrowMode: currentArrowMode, setArrowMode } = useArrowStore.getState();
            if (currentArrowMode) {
              setArrowMode(false);
              store.setActiveTool('select');
            } else {
              setArrowMode(true);
              store.setActiveTool('arrow');
            }
            break;
        }
        
        // N key: Create new sticky at viewport center
        if (e.code === 'KeyN') {
          e.preventDefault();
          const center = getViewportCenter();
          createItem({
            board_id: currentBoardId || undefined,
            item_type: 'sticky',
            position_x: Math.round(center.x / 20) * 20,
            position_y: Math.max(0, Math.round(center.y / 20) * 20),
            width: 200,
            height: 150,
            content: { text: '', color: 'yellow' },
          });
          return;
        }
      }
    };
    
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        setIsSpacePressed(false);
      }
    };

    // Handle paste event for images
    const handlePasteEvent = async (e: ClipboardEvent) => {
      if (isEditingText()) return;
      
      const clipboardData = e.clipboardData;
      if (!clipboardData) return;

      // Check for files/images
      if (clipboardData.files.length > 0) {
        e.preventDefault();
        toast.info('For images, please use drag & drop upload');
        return;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('paste', handlePasteEvent);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('paste', handlePasteEvent);
    };
  }, [isSpacePressed, isEditingText, handleSelectAll, handlePaste, getViewportCenter, animateZoomTo, fitAllItemsInView, setPan]);

  // Check if click is on a canvas item or UI element
  const isClickOnItem = useCallback((target: EventTarget | null): boolean => {
    if (!target || !(target instanceof Element)) return false;
    // Check if clicked on an item (has data-canvas-item attribute)
    return !!target.closest('[data-canvas-item]');
  }, []);

  // Handle double-click to create sticky note at position
  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    // Only on empty canvas (not on items)
    if (isClickOnItem(e.target)) return;
    if (activeTool !== 'select') return;
    if (!currentBoardId) return;
    // Don't create sticky if we just navigated into a board (the second click
    // of a board-card click can land on the new board's empty canvas).
    // Read lastBoardNavAt from the store directly — it is set synchronously
    // inside navigateToBoard so there is no useEffect-timing gap.
    const storeState = useCanvasStore.getState();
    if (storeState.isBoardTransitioning) return;
    if (storeState.currentBoardId !== currentBoardId) return;
    if (Date.now() - storeState.lastBoardNavAt < 1000) return;
    // Don't create sticky right after panning or drag-selecting — accidental
    // double-clicks at the end of those gestures shouldn't spawn a note.
    if (Date.now() - lastPanOrSelectionEndRef.current < 500) return;

    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const canvasX = (e.clientX - rect.left) / zoom - panX;
    const canvasY = (e.clientY - rect.top) / zoom - panY;

    createItem({
      board_id: currentBoardId,
      item_type: 'sticky',
      position_x: Math.round(canvasX / 20) * 20,
      position_y: Math.max(0, Math.round(canvasY / 20) * 20),
      width: 200,
      height: 150,
      content: { text: '', color: 'yellow' },
    });
  }, [zoom, panX, panY, currentBoardId, activeTool, isClickOnItem, createItem]);

  // Handle mouse for panning and selection rectangle
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Middle mouse, space+click, or pan tool for panning
    if (e.button === 1 || (e.button === 0 && isSpacePressed) || (e.button === 0 && activeTool === 'pan')) {
      e.preventDefault();
      
      // Cancel any ongoing inertia animation
      if (inertiaAnimationRef.current) {
        cancelAnimationFrame(inertiaAnimationRef.current);
        inertiaAnimationRef.current = null;
      }
      
      setIsPanning(true);
      setPanStart({ x: e.clientX - panX * zoom, y: e.clientY - panY * zoom });
      
      // Initialize velocity tracking
      velocityRef.current = { x: 0, y: 0 };
      lastPanTimeRef.current = performance.now();
      lastPanPosRef.current = { x: e.clientX, y: e.clientY };
      return;
    }

    // Check if we clicked on an item (not empty canvas)
    const clickedOnItem = isClickOnItem(e.target);

    // Arrow mode: Start drawing on empty canvas
    if (e.button === 0 && activeTool === 'arrow' && !isSpacePressed && !clickedOnItem) {
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        const canvasX = (e.clientX - rect.left) / zoom - panX;
        const canvasY = (e.clientY - rect.top) / zoom - panY;
        startDrawing(canvasX, canvasY);
      }
      return;
    }
    
    // Arrow mode: Don't drag items - let connector points handle it
    if (e.button === 0 && activeTool === 'arrow' && clickedOnItem) {
      return;
    }

    // Don't start selection when Partnr chat window is open — user is likely interacting with the chat
    if (activeChats.length > 0 && !clickedOnItem) {
      return;
    }

    // Start drag-to-select on empty canvas (both Partnr mode and normal mode)
    if (e.button === 0 && !clickedOnItem && !isSpacePressed && activeTool === 'select') {
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        const startX = e.clientX - rect.left;
        const startY = e.clientY - rect.top;
        setSelectionRect({
          startX,
          startY,
          currentX: startX,
          currentY: startY,
        });
        setIsDraggingSelection(true);
        
        // Clear normal selection when starting new selection (unless shift is held)
        if (!isPartnrMode && !e.shiftKey) {
          clearSelection();
          // Also deselect any selected arrow
          setSelectedArrow(null);
        }
      }
      return;
    }
    
    // Left click on canvas (not on items) - start drag-to-create for tools
    if (e.button === 0 && activeTool !== 'select' && activeTool !== 'pan' && activeTool !== 'arrow' && currentBoardId && !isSpacePressed && !isPartnrMode && !clickedOnItem) {
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        const canvasX = (e.clientX - rect.left) / zoom - panX;
        const canvasY = (e.clientY - rect.top) / zoom - panY;
        const snappedX = Math.round(canvasX / 20) * 20;
        const snappedY = Math.max(0, Math.round(canvasY / 20) * 20);

        // Start drag-to-create for supported tool types
        const dragCreationTools: Tool[] = ['text_card', 'sticky', 'document'];
        if (dragCreationTools.includes(activeTool)) {
          setCreationDrag({
            tool: activeTool,
            startX: e.clientX - rect.left,
            startY: e.clientY - rect.top,
            currentX: e.clientX - rect.left,
            currentY: e.clientY - rect.top,
            canvasStartX: snappedX,
            canvasStartY: snappedY,
          });
          return;
        }

        // For other tools (board, link), immediate placement
        handleToolPlacement(snappedX, snappedY);
        return;
      }
    }
    
    // Left click on canvas background for deselection (only in select mode, not dragging)
    if (e.button === 0 && activeTool === 'select' && !clickedOnItem && !isPartnrMode && !e.shiftKey) {
      // Selection will be cleared when starting selection rect (handled above)
    }
  }, [isSpacePressed, panX, panY, zoom, activeTool, currentBoardId, clearSelection, createItem, createBoard, isPartnrMode, isClickOnItem, startDrawing, setSelectedArrow, activeChats]);

  const handleToolPlacement = async (x: number, y: number, overrideTool?: Tool) => {
    const { activeTool: currentTool, setActiveTool } = useCanvasStore.getState();
    const tool = overrideTool || currentTool;
    
    switch (tool) {
      case 'sticky':
        await createItem({
          item_type: 'sticky',
          position_x: x,
          position_y: y,
          width: 200,
          height: 150,
          content: { text: '', color: 'yellow' },
        });
        break;
      case 'text_card':
        await createItem({
          item_type: 'text_card',
          position_x: x,
          position_y: y,
          width: 300,
          height: 200,
          content: { body: '' },
        });
        break;
      case 'document':
        await createItem({
          item_type: 'document',
          position_x: x,
          position_y: y,
          width: 280,
          height: 160,
          content: { title: 'Untitled Document', body: '', wordCount: 0 },
        });
        break;
      case 'board':
        await createBoard('New Board', currentBoardId, { x, y });
        // Show Mind Bubble after board creation
        handleBoardCreatedShowMindBubble();
        break;
    }
    
    // Reset to select tool after placing (only if using click method, not drag)
    if (!overrideTool) {
      setActiveTool('select');
    }
  };

  // Handle tool placement with custom size (from drag-to-create)
  const handleToolPlacementWithSize = async (x: number, y: number, w: number, h: number, tool: Tool) => {
    const { setActiveTool } = useCanvasStore.getState();

    switch (tool) {
      case 'sticky':
        await createItem({
          item_type: 'sticky',
          position_x: x,
          position_y: y,
          width: w,
          height: h,
          content: { text: '', color: 'yellow' },
        });
        break;
      case 'text_card':
        await createItem({
          item_type: 'text_card',
          position_x: x,
          position_y: y,
          width: w,
          height: h,
          content: { body: '' },
        });
        break;
      case 'document':
        await createItem({
          item_type: 'document',
          position_x: x,
          position_y: y,
          width: w,
          height: h,
          content: { title: 'Untitled Document', body: '', wordCount: 0 },
        });
        break;
    }

    setActiveTool('select');
  };

  // Handle tool drag from toolbar
  const handleToolDragOver = useCallback((e: React.DragEvent) => {
    if (e.dataTransfer.types.includes('application/x-tool-type')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      setIsToolDragOver(true);
    }
  }, []);
  
  const handleToolDragLeave = useCallback(() => {
    setIsToolDragOver(false);
  }, []);
  
  const handleToolDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setIsToolDragOver(false);
    setDraggingFromToolbar(false, null);
    
    // Check for shared board drop first
    const jsonData = e.dataTransfer.getData('application/json');
    if (jsonData) {
      try {
        const parsed = JSON.parse(jsonData);
        if (parsed.type === 'shared_board' && parsed.board && currentBoardId) {
          const share = parsed.board as SharedBoard;
          
          const rect = containerRef.current?.getBoundingClientRect();
          if (rect) {
            const canvasX = (e.clientX - rect.left) / zoom - panX;
            const canvasY = (e.clientY - rect.top) / zoom - panY;
            const snappedX = Math.round(canvasX / 20) * 20;
            const snappedY = Math.max(0, Math.round(canvasY / 20) * 20);
            
            // Create board_link item for the shared board
            await createItem({
              board_id: currentBoardId,
              item_type: 'board_link',
              position_x: snappedX,
              position_y: snappedY,
              width: 200,
              height: 150,
              content: {
                linked_board_id: share.board.id,
                name: share.board.name,
                icon_color: share.board.icon_color,
                is_shared: true,
                share_id: share.id,
                permission: share.permission
              }
            });
            
            toast.success(`Added "${share.board.name}" to canvas`);
            setShowSharedBoardsModal(false);
            return;
          }
        }
      } catch (err) {
        // Not valid JSON, continue to tool handling
      }
    }
    
    const toolType = e.dataTransfer.getData('application/x-tool-type') as Tool;
    if (!toolType || !currentBoardId) {
      return;
    }
    
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      const canvasX = (e.clientX - rect.left) / zoom - panX;
      const canvasY = (e.clientY - rect.top) / zoom - panY;
      const snappedX = Math.round(canvasX / 20) * 20;
      const snappedY = Math.max(0, Math.round(canvasY / 20) * 20);
      
      await handleToolPlacement(snappedX, snappedY, toolType);
    }
  }, [currentBoardId, zoom, panX, panY, createItem]);
  
  // Store last mouse position for coordinate-based drop detection
  const lastMousePosRef = useRef({ x: 0, y: 0 });
  
  // Batched selection rect update ref
  const pendingSelectionRef = useRef<{ currentX: number; currentY: number } | null>(null);

  // Process batched mouse updates in animation frame
  const processBatchedMouseUpdate = useCallback(() => {
    mouseMoveRafRef.current = null;

    const pending = pendingMouseRef.current;
    if (!pending) return;

    if (isPanning) {
      const now = performance.now();
      const deltaTime = now - lastPanTimeRef.current;

      // Calculate velocity based on mouse movement
      if (deltaTime > 0) {
        const dx = pending.x - lastPanPosRef.current.x;
        const dy = pending.y - lastPanPosRef.current.y;

        // Smooth velocity with exponential moving average
        velocityRef.current = {
          x: velocityRef.current.x * 0.5 + (dx / deltaTime) * VELOCITY_SCALE * 0.5,
          y: velocityRef.current.y * 0.5 + (dy / deltaTime) * VELOCITY_SCALE * 0.5,
        };
      }

      lastPanTimeRef.current = now;
      lastPanPosRef.current = { x: pending.x, y: pending.y };

      // Read panStart from ref to avoid stale closure
      const currentPanStart = panStartRef.current;
      const newPanX = (pending.x - currentPanStart.x) / zoom;
      const newPanY = (pending.y - currentPanStart.y) / zoom;
      setPan(newPanX, newPanY);
    }

    // Process batched selection rect update — read from ref to avoid stale closure
    const pendingSelection = pendingSelectionRef.current;
    const currentSelectionRect = selectionRectRef.current;
    if (pendingSelection && isDraggingSelection && currentSelectionRect) {
      setSelectionRect({
        ...currentSelectionRect,
        currentX: pendingSelection.currentX,
        currentY: pendingSelection.currentY,
      });
      pendingSelectionRef.current = null;
    }

    pendingMouseRef.current = null;
  }, [isPanning, zoom, setPan, isDraggingSelection]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    // Always track mouse position synchronously for coordinate-based drop detection
    lastMousePosRef.current = { x: e.clientX, y: e.clientY };
    
    // Update arrow draft while drawing
    if (isDrawingArrow) {
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        const canvasX = (e.clientX - rect.left) / zoom - panX;
        const canvasY = (e.clientY - rect.top) / zoom - panY;
        updateDrawing(canvasX, canvasY);
      }
      return; // Don't process panning or selection while drawing arrows
    }
    
    // Store pending position for batched update
    pendingMouseRef.current = { x: e.clientX, y: e.clientY };
    
    // Store pending selection rect update
    if (isDraggingSelection) {
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        pendingSelectionRef.current = {
          currentX: e.clientX - rect.left,
          currentY: e.clientY - rect.top,
        };
      }
    }

    // Update creation drag rect
    if (creationDrag) {
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        setCreationDrag(prev => prev ? {
          ...prev,
          currentX: e.clientX - rect.left,
          currentY: e.clientY - rect.top,
        } : null);
      }
    }
    
    // Request animation frame if not already pending
    if (!mouseMoveRafRef.current) {
      mouseMoveRafRef.current = requestAnimationFrame(processBatchedMouseUpdate);
    }
  }, [isDraggingSelection, processBatchedMouseUpdate, isDrawingArrow, zoom, panX, panY, updateDrawing]);

  const handleMouseUp = useCallback(async (e?: React.MouseEvent | MouseEvent) => {
    // Cancel any pending batched mouse updates
    if (mouseMoveRafRef.current) {
      cancelAnimationFrame(mouseMoveRafRef.current);
      mouseMoveRafRef.current = null;
    }
    pendingMouseRef.current = null;
    pendingSelectionRef.current = null;
    
    // Finish arrow drawing
    if (isDrawingArrow) {
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect && e) {
        const canvasX = (e.clientX - rect.left) / zoom - panX;
        const canvasY = (e.clientY - rect.top) / zoom - panY;
        
        // Check if we're hovering over a connector point
        const { hoveredConnector } = useArrowStore.getState();
        if (hoveredConnector && currentBoardId) {
          await finishDrawing(currentBoardId, hoveredConnector.itemId, hoveredConnector.position);
        } else if (currentBoardId) {
          await finishDrawing(currentBoardId);
        } else {
          cancelDrawing();
        }
      } else {
        cancelDrawing();
      }
      return;
    }
    
    // Start inertia animation when panning ends
    if (isPanning) {
      startInertiaAnimation();
      lastPanOrSelectionEndRef.current = Date.now();
    }

    setIsPanning(false);

    // Handle drag-to-board drop using COORDINATE-BASED collision detection
    const dragState = useDragToBoardStore.getState();
    if (dragState.isDragging && dragState.draggedItemIds.length > 0) {
      // Get mouse position - prefer event coords, fallback to stored position
      const mouseX = e?.clientX ?? lastMousePosRef.current.x;
      const mouseY = e?.clientY ?? lastMousePosRef.current.y;
      
      // Check if mouse is over canvas area (if outside, let UI elements handle it)
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        const isInsideCanvas = 
          mouseX >= rect.left && 
          mouseX <= rect.right && 
          mouseY >= rect.top && 
          mouseY <= rect.bottom;
        
        if (isInsideCanvas) {
          // Convert mouse position to canvas coordinates
          const canvasX = (mouseX - rect.left) / zoom - panX;
          const canvasY = (mouseY - rect.top) / zoom - panY;
          
          console.log('Drop detection - mouse at canvas coords:', { canvasX, canvasY });
          
          // Find board card at drop position (coordinate-based, not event-based)
          const targetBoardId = findBoardCardAtPoint(canvasX, canvasY, dragState.draggedItemIds);
          
          if (targetBoardId) {
            console.log('Coordinate-based drop detected! Target board:', targetBoardId);
            await moveItemsToBoard([...dragState.draggedItemIds], targetBoardId);
            dragState.reset();
          } else {
            // No board found within canvas - just end the drag (item stays where it is)
            console.log('No board at drop location within canvas - ending drag');
            dragState.endDrag();
          }
        } else {
          // Mouse is outside canvas - delay reset to let sidebar/breadcrumbs handle drop
          // The UI component handlers will call reset() if they process the drop
          // Otherwise, reset after a short delay to clean up
          console.log('Mouse outside canvas area - checking for UI element drops');
          setTimeout(() => {
            const currentState = useDragToBoardStore.getState();
            if (currentState.isDragging) {
              console.log('No UI element handled the drop - resetting drag state');
              currentState.endDrag();
            }
          }, 50);
        }
      } else {
        dragState.endDrag();
      }
    }

    // Handle creation drag end
    const currentCreationDrag = creationDragRef.current;
    if (currentCreationDrag && currentBoardId) {
      // Clear ref immediately so the window-level mouseup listener (which also
      // calls this function) sees null and doesn't create a second item.
      creationDragRef.current = null;
      const dragDistX = Math.abs(currentCreationDrag.currentX - currentCreationDrag.startX);
      const dragDistY = Math.abs(currentCreationDrag.currentY - currentCreationDrag.startY);
      const MIN_CREATION_DRAG = 20; // pixels

      if (dragDistX > MIN_CREATION_DRAG || dragDistY > MIN_CREATION_DRAG) {
        // User dragged — create with custom dimensions
        const canvasEndX = currentCreationDrag.currentX / zoom - panX;
        const canvasEndY = currentCreationDrag.currentY / zoom - panY;
        const x1 = Math.min(currentCreationDrag.canvasStartX, Math.round(canvasEndX / 20) * 20);
        const y1 = Math.max(0, Math.min(currentCreationDrag.canvasStartY, Math.round(canvasEndY / 20) * 20));
        const w = Math.max(60, Math.abs(Math.round((canvasEndX - currentCreationDrag.canvasStartX) / 20) * 20));
        const h = Math.max(60, Math.abs(Math.round((canvasEndY - currentCreationDrag.canvasStartY) / 20) * 20));

        handleToolPlacementWithSize(x1, y1, w, h, currentCreationDrag.tool);
      } else {
        // Single click — use default size
        handleToolPlacement(currentCreationDrag.canvasStartX, currentCreationDrag.canvasStartY, currentCreationDrag.tool);
      }
      setCreationDrag(null);
    }

    // Handle selection end
    if (isDraggingSelection) {
      if (isPartnrMode) {
        handlePartnrSelectionEnd();
      } else {
        handleNormalSelectionEnd();
      }
      setSelectionRect(null);
      setIsDraggingSelection(false);
      lastPanOrSelectionEndRef.current = Date.now();
    }
  }, [isDraggingSelection, isPartnrMode, handlePartnrSelectionEnd, handleNormalSelectionEnd, moveItemsToBoard, zoom, panX, panY, findBoardCardAtPoint, isPanning, startInertiaAnimation, isDrawingArrow, currentBoardId, finishDrawing, cancelDrawing]);

  // Lightweight mouse-leave handler — only stops panning without triggering
  // drag-to-board drops, selection finalization, or arrow drawing.
  // The global window mouseup listener handles the real mouseup if it happens outside.
  const handleMouseLeave = useCallback(() => {
    // Cancel batched mouse updates
    if (mouseMoveRafRef.current) {
      cancelAnimationFrame(mouseMoveRafRef.current);
      mouseMoveRafRef.current = null;
    }
    pendingMouseRef.current = null;

    // If panning, start inertia and stop panning
    if (isPanning) {
      startInertiaAnimation();
      setIsPanning(false);
    }
  }, [isPanning, startInertiaAnimation]);

  // Ensure we always capture mouseup even if it happens outside the canvas
  useEffect(() => {
    const onWindowMouseUp = (e: MouseEvent) => {
      void handleMouseUp(e);
    };
    const onWindowMouseMove = (e: MouseEvent) => {
      // Track mouse even outside canvas for accurate drop detection
      lastMousePosRef.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener('mouseup', onWindowMouseUp);
    window.addEventListener('mousemove', onWindowMouseMove);
    return () => {
      window.removeEventListener('mouseup', onWindowMouseUp);
      window.removeEventListener('mousemove', onWindowMouseMove);
    };
  }, [handleMouseUp]);

  // Cleanup animations on unmount
  useEffect(() => {
    return () => {
      if (inertiaAnimationRef.current) {
        cancelAnimationFrame(inertiaAnimationRef.current);
      }
      if (zoomAnimationRef.current) {
        cancelAnimationFrame(zoomAnimationRef.current);
      }
      if (mouseMoveRafRef.current) {
        cancelAnimationFrame(mouseMoveRafRef.current);
      }
    };
  }, []);

  // Cursor style
  const getCursor = () => {
    if (isPanning) return 'grabbing';
    if (isSpacePressed || activeTool === 'pan') return 'grab';
    if (activeTool !== 'select') return 'crosshair';
    if (isDraggingSelection) return 'crosshair';
    return 'default';
  };

  // Calculate drop position for drag-and-drop uploads
  const getDropPosition = useCallback((e: React.DragEvent) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      const canvasX = (e.clientX - rect.left) / zoom - panX;
      const canvasY = (e.clientY - rect.top) / zoom - panY;
      const snappedX = Math.round(canvasX / 20) * 20;
      const snappedY = Math.round(canvasY / 20) * 20;
      return { x: snappedX, y: snappedY };
    }
    return { x: 100, y: 100 };
  }, [zoom, panX, panY]);

  // Get click position for context menu
  const getClickPosition = useCallback((e: React.MouseEvent) => {
    return screenToCanvas(e.clientX, e.clientY);
  }, [screenToCanvas]);

  return (
    <DropZone mode="canvas" getDropPosition={getDropPosition}>
      <CanvasContextMenu 
        onPaste={handlePaste} 
        onSelectAll={handleSelectAll}
        onRescueStrandedItems={() => {
          const count = rescueStrandedItems();
          if (count > 0) {
            toast.success(`Rescued ${count} hidden item${count > 1 ? 's' : ''}`);
          } else {
            toast.info('No hidden items found');
          }
        }}
        getClickPosition={getClickPosition}
      >
        <motion.div
          ref={containerRef}
          data-canvas-export-target
          className={`relative w-full h-full overflow-hidden canvas-grid bg-canvas transition-colors ${
            isToolDragOver || isDraggingFromToolbar ? 'ring-2 ring-primary ring-inset bg-primary/5' : ''
          }`}
          style={{
            cursor: getCursor(),
            touchAction: 'none', // We handle all touch gestures ourselves
            // Task 1.6: Disable text selection during pan mode or active drag
            ...(isPanning || isSpacePressed || activeTool === 'pan'
              ? { userSelect: 'none', WebkitUserSelect: 'none' } as React.CSSProperties
              : {}),
          }}
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onDoubleClick={handleDoubleClick}
          onDragOver={handleToolDragOver}
          onDragLeave={handleToolDragLeave}
          onDrop={handleToolDrop}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
        >
          {/* Grid overlay */}
          {isGridVisible && (
            <CanvasGrid panX={panX} panY={panY} zoom={zoom} />
          )}
          
          {/* Breadcrumbs rendered by Canvas.tsx (fixed position above canvas) — not here */}
          
          
          {/* Shared Boards Button - top right */}
          <div data-export-ignore="true">
            <SharedBoardsButton onClick={() => setShowSharedBoardsModal(true)} />
          </div>
          
          {/* Cluster toggle button - top right, aligned with Home button */}
          <div data-export-ignore="true">
            <ClusterToggleButton />
          </div>
          {/* Canvas transform layer */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentBoardId || 'root'}
              data-canvas-container="true"
              className="absolute inset-0 origin-top-left transition-none pointer-events-none"
              style={{
                transform: `scale(${zoom}) translate(${panX}px, ${panY}px)`,
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: isBoardTransitioning ? 0.5 : 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
            >
              {/* Spatial cluster boundaries */}
              <ClusterOverlay zoom={zoom} panX={panX} panY={panY} />

              {/* Render only visible items (viewport culling) - each item has pointer-events-auto */}
              {visibleItems.map((item) => (
                <div key={item.id} className="pointer-events-auto">
                  <CanvasItem item={item} zoom={zoom} isPanMode={activeTool === 'pan' || isSpacePressed} lodTier={lodTier} />
                </div>
              ))}

              {/* Empty state */}
              {!isLoading && items.length === 0 && !isBoardTransitioning && (
                <div
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center pointer-events-none"
                  style={{ transform: `translate(-50%, -50%) scale(${1/zoom})` }}
                >
                  <div className="text-muted-foreground text-lg">
                    Drop something here to get started
                  </div>
                  <div className="text-muted-foreground/60 text-sm mt-2">
                    Use the toolbar below or drag files here
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
          
          {/* Creation drag preview rectangle */}
          {creationDrag && (
            <div
              className="absolute border-2 border-dashed border-primary/60 bg-primary/5 rounded-lg pointer-events-none z-30"
              style={{
                left: Math.min(creationDrag.startX, creationDrag.currentX),
                top: Math.min(creationDrag.startY, creationDrag.currentY),
                width: Math.abs(creationDrag.currentX - creationDrag.startX),
                height: Math.abs(creationDrag.currentY - creationDrag.startY),
              }}
            />
          )}

          {/* Selection Rectangle - positioned relative to container (screen coords) */}
          {selectionRect && isDraggingSelection && (
            <PartnrSelectionRect
              rect={selectionRect}
              zoom={zoom}
              panX={panX}
              panY={panY}
              isPartnrMode={isPartnrMode}
            />
          )}
          
          {/* Partnr Selection Pill - appears below selected items */}
          <div data-export-ignore="true">
            <PartnrSelectionPill
              zoom={zoom}
              panX={panX}
              panY={panY}
              containerRect={containerRef.current?.getBoundingClientRect() ?? null}
            />
          </div>
          
          {/* Partnr AI connection lines between source and generated items */}
          <PartnrConnectionLines zoom={zoom} panX={panX} panY={panY} />
          
          {/* Arrow Layer - renders all arrows and draft arrow */}
          <ArrowLayer zoom={zoom} panX={panX} panY={panY} />

          {/* Arrow properties panel - shows when an arrow is selected */}
          <ArrowPropertiesPanel />

          {/* Minimap - hidden for now */}
          {/* <Minimap /> */}
          
          {/* Partnr Chat Windows - only show the first active chat (single window) */}
          {activeChats.length > 0 && (
            <div data-export-ignore="true">
              <PartnrChatWindow key={activeChats[0].id} chat={activeChats[0]} />
            </div>
          )}
          
          {/* Drag to board visuals */}
          <div data-export-ignore="true">
            <DragGhostPreview />
            <DragOverlay />
            <DragToBoardDebugButton />
          </div>
        </motion.div>
      </CanvasContextMenu>
      
      {/* Keyboard shortcuts help overlay */}
      <KeyboardShortcutsHelp 
        open={showShortcutsHelp} 
        onOpenChange={setShowShortcutsHelp} 
      />
      
      {/* Shared Boards Modal */}
      {showSharedBoardsModal && (
        <SharedBoardsModal
          boards={sharedBoards}
          onClose={() => setShowSharedBoardsModal(false)}
        />
      )}
      
      {/* Mind Bubble - floating pinned items launcher */}
      {showMindBubble && (
        <MindBubble
          onClose={() => setShowMindBubble(false)}
          onDragItem={handleMindBubbleDrop}
        />
      )}
      
      {/* Sync Choice Modal */}
      {syncModalState?.isOpen && (
        <SyncChoiceModal
          isOpen={syncModalState.isOpen}
          onClose={() => setSyncModalState(null)}
          itemName={
            (syncModalState.item.content as any)?.title ||
            (syncModalState.item.content as any)?.text ||
            (syncModalState.item.content as any)?.body?.slice(0, 20) ||
            'This item'
          }
          sourceBoardName={syncModalState.sourceBoardName}
          onDuplicate={handleMindBubbleDuplicate}
          onSync={handleMindBubbleSync}
        />
      )}
    </DropZone>
  );
}
